import { useMutationHook, useQueryHook } from "@/hooks"
import {
  createStadium,
  readStadiumByActivity,
  readStadiumByTeamAndPeriod,
  readStadiumInfo,
  readStadiumList,
  readStadiumListByMapBoundary,
} from "@/app/api/stadium"
import {
  ReadStadiumByTeamAndPeriodParams,
  ReadStadiumListByBoundaryParams,
  ReadStadiumListParams,
  ReadStadiumListResponse,
  Stadium,
} from "@/types/stadium"
import { UseQueryOptions } from "@tanstack/react-query"
import { ApiError } from "@/app/api/type"

export const useStadiumListQuery = (params: ReadStadiumListParams) => {
  return useQueryHook({
    queryKey: ["readStadiumList", params.name, params.page, params.latitude, params.longitude],
    queryFn: () => readStadiumList(params),
    options: {
      staleTime: 0,
    } as UseQueryOptions<ReadStadiumListResponse, ApiError>,
  })
}

export const useStadiumBoundaryListQuery = (params: ReadStadiumListByBoundaryParams) => {
  return useQueryHook({
    queryKey: ["readStadiumListByBoundary", ...Object.values(params)],
    queryFn: () => readStadiumListByMapBoundary(params),
    options: {
      enabled:
        params.minLat !== undefined &&
        params.maxLat !== undefined &&
        params.minLng !== undefined &&
        params.maxLng !== undefined,
    } as UseQueryOptions<Stadium[], ApiError>,
  })
}

export const useStadiumInfoQuery = (idfStadium: number) => {
  return useQueryHook({
    queryKey: ["readStadiumInfo", idfStadium],
    queryFn: () => readStadiumInfo(idfStadium),
    options: {
      enabled: !!idfStadium,
    } as UseQueryOptions<Stadium, ApiError>,
  })
}

export const useStadiumByActivityQuery = (idfActivity?: number) => {
  return useQueryHook({
    queryKey: ["readStadiumByActivity", idfActivity],
    queryFn: () => readStadiumByActivity(idfActivity as number),
    options: {
      enabled: !!idfActivity,
    } as UseQueryOptions<Stadium, ApiError>,
  })
}

export const useStadiumByTeamAndPeriodQuery = (params: ReadStadiumByTeamAndPeriodParams) => {
  return useQueryHook({
    queryKey: ["readStadiumByTeamAndPeriod", ...Object.values(params)],
    queryFn: () => readStadiumByTeamAndPeriod(params),
    options: {
      enabled:
        params.idfTeam !== undefined && !isNaN(params.dateStart) && !isNaN(params.dateFinish),
    } as UseQueryOptions<Stadium, ApiError>,
  })
}

export const useCreateStadiumMutation = () => {
  return useMutationHook(createStadium, [
    "readStadiumList",
    "readStadiumListByBoundary",
    "readStadiumByTeamAndPeriod",
  ])
}
